/**
 * WCAG 2.4.9 Link Purpose (Link Only) — Level AAA.
 * Fixes the scan finding "Several links on a page share the same link text,
 * but go to different destinations" (e.g. a navigation entry and a product
 * card vendor both reading "Cat Litter Disposal System", or a footer
 * "Privacy Policy" next to the one rendered inside a reviews widget).
 *
 * Problem: menu entries, "View all" / "Learn more" labels and similar link
 * texts are configured by merchandising in the Shopify admin. Asking them to
 * write descriptive accessible names would bloat every visible label, and
 * runtime-injected app content (reviews, subscription widgets, consent
 * banners) cannot be relabelled from the theme at all.
 *
 * How it works (fully content-driven — no specific link is hardcoded):
 *  - All <a href> elements on the page are grouped by their ACCESSIBLE name
 *    (aria-label when present, otherwise link text plus image alt text,
 *    case/whitespace-normalized).
 *  - Inside a group, links pointing to the SAME destination are fine; the
 *    group is ambiguous only when two or more DISTINCT destinations exist.
 *    Each destination subgroup then gets one qualifier derived from its
 *    destination — "product page", "collection", "page", "blog post",
 *    "search results" or "external site" — applied to every member, so the
 *    accessible names become unique while visible text stays as configured.
 *  - If the destination types don't separate the subgroups (e.g. two product
 *    links with identical titles), the humanized URL slug is used instead.
 *  - Subgroups the first two passes cannot describe — bare external origins
 *    ("https://developers.facebook.com/"), the site root ("/") or identical
 *    slugs on different hosts — fall back to the host name or the full
 *    destination URL (neither needs translation) instead of voiding the whole
 *    group; a group that stays ambiguous even after that is left untouched.
 *  - Visible text is never changed: the qualifier is a sr-only span appended
 *    inside the link, or appended to aria-label when the link already has
 *    one. Links whose name is owned by aria-labelledby are skipped.
 *  - Live content is re-grouped through a MutationObserver (debounced via
 *    requestAnimationFrame), so links rendered after page load by review,
 *    subscription or consent apps are disambiguated as they appear; already
 *    annotated links keep their original name for re-grouping.
 *  - Opt-out: put data-link-disambiguate="off" on any subtree that must
 *    never be touched.
 *  - Manual re-scan hook for QA: window.petkitA11yDuplicateLinks.scan()
 *
 * @see https://www.w3.org/WAI/WCAG22/Understanding/link-purpose-link-only.html
 */
(function () {
  "use strict";

  if (window.petkitA11yDuplicateLinks) return; // guard against double inclusion

  var STATE_ATTR = "data-dup-link-state"; // "1" once annotated
  var NAME_ATTR = "data-dup-link-name"; // original normalized name, for re-grouping
  var LABEL_ATTR = "data-dup-link-label"; // original aria-label, when rewritten
  var OPT_OUT_SELECTOR = "[data-link-disambiguate='off']";
  var QUALIFIER_CLASS = "a11y-link-qualifier";

  /* Qualifier labels come from theme translations (locales/*.json via
     snippets/js-variables.liquid); English is only a last-resort fallback. */
  function getStrings() {
    var strings = (window.themeVariables && window.themeVariables.strings) || {};
    return {
      product: strings.linkTypeProduct || "product page",
      collection: strings.linkTypeCollection || "collection",
      page: strings.linkTypePage || "page",
      article: strings.linkTypeArticle || "blog post",
      search: strings.linkTypeSearch || "search results",
      external: strings.linkTypeExternal || "external site"
    };
  }

  function injectStyles() {
    if (document.getElementById("a11y-duplicate-links-styles")) return;
    var style = document.createElement("style");
    style.id = "a11y-duplicate-links-styles";
    style.textContent =
      "." + QUALIFIER_CLASS +
      "{position:absolute!important;width:1px;height:1px;padding:0;margin:-1px;" +
      "overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;border:0;}";
    document.head.appendChild(style);
  }

  /** Accessible name of a link, or null when it is not ours to change. */
  function accessibleName(link) {
    if (link.getAttribute("aria-labelledby")) return null; // name owned elsewhere
    var labelled = link.getAttribute("aria-label");
    if (labelled !== null && labelled.trim() !== "") {
      return labelled.replace(/\s+/g, " ").trim();
    }
    var parts = [link.textContent || ""];
    var imgs = link.querySelectorAll("img[alt]");
    for (var i = 0; i < imgs.length; i++) parts.push(imgs[i].getAttribute("alt") || "");
    return parts.join(" ").replace(/\s+/g, " ").trim();
  }

  function isProcessable(link) {
    if (link.namespaceURI && link.namespaceURI !== "http://www.w3.org/1999/xhtml") return false;
    if (!link.hasAttribute("href")) return false;
    if (/^(mailto:|tel:|javascript:|data:)/i.test(link.getAttribute("href"))) return false;
    if (link.getAttribute("aria-hidden") === "true") return false;
    var role = link.getAttribute("role");
    if (role && role !== "link") return false;
    return true;
  }

  /** Destination metadata: localized type label plus humanized slug fallback. */
  function parseDestination(link, strings) {
    var url;
    try {
      url = new URL(link.href, location.href);
    } catch (e) {
      return null;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return null;

    var external = location.host !== "" && url.host !== location.host;
    var segments = url.pathname.split("/").filter(Boolean);
    var first = segments[0] || "";
    var slug = segments.length
      ? decodeURIComponent(segments[segments.length - 1]).replace(/[-_]+/g, " ").trim()
      : "";

    var type = null;
    if (external) {
      type = strings.external;
    } else if (first === "products") {
      type = strings.product;
    } else if (first === "collections") {
      type = strings.collection;
    } else if (first === "pages") {
      type = strings.page;
    } else if (first === "blogs") {
      type = strings.article;
    } else if (first === "search") {
      type = strings.search;
    }

    return { type: type, slug: slug, external: external, host: url.hostname };
  }

  /** "www.developers.facebook.com" -> "developers facebook com" */
  function hostLabel(info) {
    return info.host.replace(/^www\./, "").replace(/[-_.]+/g, " ").trim();
  }

  /**
   * Candidates for a subgroup the type/slug passes left null or duplicated,
   * most specific first: slug, host name (external only — an internal host
   * adds nothing), full destination URL. Host names and URLs need no
   * translation, so this pass introduces no new theme strings.
   */
  function qualifierCandidates(members) {
    var candidates = [];
    for (var i = 0; i < members.length; i++) {
      var info = members[i].info;
      if (info && info.slug) candidates.push(info.slug);
    }
    for (var n = 0; n < members.length; n++) {
      if (members[n].info && members[n].info.external && members[n].info.host) {
        candidates.push(hostLabel(members[n].info));
        break;
      }
    }
    for (var k = 0; k < members.length; k++) {
      try {
        var url = new URL(members[k].entry.link.href, location.href);
        var label = (url.hostname + " " + url.pathname + " " + url.search)
          .replace(/^www\./, "")
          .replace(/[-_./?=&]+/g, " ")
          .replace(/\s+/g, " ")
          .trim();
        if (label) candidates.push(label);
      } catch (e) {}
      if (candidates.length) break;
    }
    return candidates;
  }

  function destinationKey(link) {
    try {
      var url = new URL(link.href, location.href);
      // trailing slash is cosmetic; hash/query are part of the destination
      return url.href.replace(/\/(?=\?|#|$)/, "");
    } catch (e) {
      return "\u0000unparsable";
    }
  }

  function distinctCount(values, keyFn) {
    var seen = {};
    var count = 0;
    for (var i = 0; i < values.length; i++) {
      var key = keyFn(values[i]).toLowerCase();
      if (!seen[key]) {
        seen[key] = true;
        count++;
      }
    }
    return count;
  }

  function annotate(link, originalName, qualifier) {
    var label = link.getAttribute("aria-label");
    if (label !== null && label.trim() !== "") {
      link.setAttribute(LABEL_ATTR, label);
      link.setAttribute("aria-label", label + " (" + qualifier + ")");
    } else {
      var span = document.createElement("span");
      span.className = QUALIFIER_CLASS;
      span.textContent = " (" + qualifier + ")";
      link.appendChild(span);
    }
    link.setAttribute(STATE_ATTR, "1");
    link.setAttribute(NAME_ATTR, originalName);
  }

  function scan() {
    var strings = getStrings();
    var links = document.querySelectorAll("a[href]");
    var groups = {}; // normalized name -> [{ link, name }]
    var order = [];

    for (var i = 0; i < links.length; i++) {
      var link = links[i];
      if (!isProcessable(link)) continue;

      // Previously annotated links re-group under their ORIGINAL name so a
      // later-arriving twin (app-rendered) joins the same group.
      var name = link.hasAttribute(NAME_ATTR)
        ? link.getAttribute(NAME_ATTR)
        : accessibleName(link);
      if (!name) continue;

      var key = name.toLowerCase();
      if (!groups[key]) {
        groups[key] = [];
        order.push(key);
      }
      // Opted-out links still take part in collision detection (screen readers
      // hear them too) — they are just never annotated themselves.
      groups[key].push({
        link: link,
        name: name,
        optOut: !!link.closest(OPT_OUT_SELECTOR)
      });
    }

    for (var g = 0; g < order.length; g++) {
      var entries = groups[order[g]];
      if (entries.length < 2) continue;

      // Partition by destination: links to the same destination never
      // conflict with each other, distinct destinations do.
      var byDest = {};
      var destOrder = [];
      var infos = [];
      for (var e = 0; e < entries.length; e++) {
        var info = parseDestination(entries[e].link, strings);
        infos.push(info);
        var destKey = destinationKey(entries[e].link);
        if (!byDest[destKey]) {
          byDest[destKey] = [];
          destOrder.push(destKey);
        }
        byDest[destKey].push(e);
      }
      if (destOrder.length < 2) continue; // same destination everywhere

      // One qualifier per destination subgroup: the shared destination type,
      // falling back to the humanized URL slug of a member.
      var subgroups = destOrder.map(function (destKey) {
        return byDest[destKey].map(function (index) {
          return { entry: entries[index], info: infos[index] };
        });
      });
      var qualifiers = subgroups.map(function (members) {
        var types = [];
        for (var m = 0; m < members.length; m++) {
          var type = members[m].info ? members[m].info.type : null;
          if (type && types.indexOf(type) === -1) types.push(type);
        }
        if (types.length === 1) return types[0];
        for (var n = 0; n < members.length; n++) {
          if (members[n].info && members[n].info.slug) return members[n].info.slug;
        }
        return null;
      });

      // Types alone not separating the subgroups (e.g. two product links with
      // identical titles)? Slugs for every subgroup instead.
      if (distinctCount(qualifiers, function (q) { return q || "\u0000null"; }) !== qualifiers.length) {
        qualifiers = subgroups.map(function (members) {
          for (var m = 0; m < members.length; m++) {
            if (members[m].info && members[m].info.slug) return members[m].info.slug;
          }
          return null;
        });
      }

      // Subgroups left null or duplicated here (site root "/", bare external
      // origins, identical slugs on different hosts) used to void the whole
      // group. Re-describe only those from their candidate list, skipping
      // labels already taken, so every subgroup ends up with a unique qualifier.
      var counts = {};
      for (var c = 0; c < qualifiers.length; c++) {
        var countKey = (qualifiers[c] || "\u0000null").toLowerCase();
        counts[countKey] = (counts[countKey] || 0) + 1;
      }
      var taken = {};
      for (var t = 0; t < qualifiers.length; t++) {
        var kept = qualifiers[t];
        if (kept !== null && counts[kept.toLowerCase()] === 1) taken[kept.toLowerCase()] = true;
      }
      for (var r = 0; r < subgroups.length; r++) {
        var current = qualifiers[r];
        if (current !== null && counts[current.toLowerCase()] === 1) continue;
        var candidates = qualifierCandidates(subgroups[r]);
        var chosen = null;
        for (var cd = 0; cd < candidates.length; cd++) {
          var candidateKey = candidates[cd].toLowerCase();
          if (!taken[candidateKey]) {
            chosen = candidates[cd];
            taken[candidateKey] = true;
            break;
          }
        }
        if (chosen === null) break; // nothing describes this subgroup — leave the group alone
        qualifiers[r] = chosen;
      }
      if (distinctCount(qualifiers, function (q) { return q || "\u0000null"; }) !== qualifiers.length) continue;

      for (var s = 0; s < subgroups.length; s++) {
        for (var k = 0; k < subgroups[s].length; k++) {
          var member = subgroups[s][k];
          if (member.entry.optOut) continue; // configured off — never touched
          if (!member.entry.link.hasAttribute(STATE_ATTR)) {
            annotate(member.entry.link, member.entry.name, qualifiers[s]);
          }
        }
      }
    }
  }

  function setupObserver() {
    var scheduled = false;
    var observer = new MutationObserver(function () {
      if (scheduled) return;
      scheduled = true;
      window.requestAnimationFrame(function () {
        scheduled = false;
        scan();
      });
    });
    observer.observe(document.documentElement, { childList: true, subtree: true });
  }

  function init() {
    injectStyles();
    scan();
    setupObserver();
  }

  window.petkitA11yDuplicateLinks = { scan: scan };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
